import React from 'react';
import { Droplets, TrendingUp, Layers, CircleDollarSign, Package, Square } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета расхода краски
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета расхода краски">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Необходимый объем краски */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Droplets className="w-4 h-4"/>
            Расход краски (точный)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatNumber(results.exactPaintVolume, 2)} л
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Для покраски {formatNumber(results.totalArea, 1)} м² в {results.coatsNumber} {results.coatsNumber === 1 ? 'слой' : results.coatsNumber < 5 ? 'слоя' : 'слоев'}
          </p>
        </div>

        {/* Рекомендуемый объем с запасом */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <Package className="w-4 h-4"/>
            Рекомендуемый объем
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatNumber(results.recommendedVolume, 2)} л
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              (с запасом 15%)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Стоимость: {formatNumber(results.totalCost, 0)} ₽
          </p>
        </div>
      </div>

      {/* Детали оптимального количества банок */}
      <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800">
        <h3 className="text-base font-medium text-gray-900 dark:text-white mb-3 flex items-center">
          <Package className="w-5 h-5 mr-2 text-green-500" />
          Рекомендуемое количество банок
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {results.recommendedCans.map((can) => (
            <div 
              key={can.size}
              className={`p-3 rounded-lg border ${
                can.size === results.optimalCans.size 
                  ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700' 
                  : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {can.size} л × {can.count} {can.count === 1 ? 'банка' : can.count < 5 ? 'банки' : 'банок'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Объем: {formatNumber(can.totalVolume, 1)} л
              </div>
              <div className={`text-xs font-medium mt-1 ${
                can.size === results.optimalCans.size 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {formatNumber(can.cost, 0)} ₽
              </div>
              {can.size === results.optimalCans.size && (
                <div className="mt-2 text-xs text-green-600 dark:text-green-400 font-medium">
                  ✓ Оптимальный вариант
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Общая площадь покраски" 
          value={`${formatNumber(results.totalArea, 2)} м²`} 
          color="indigo" 
          icon={<Square className="w-4 h-4"/>}
        />
        
        {results.wallsArea && (
          <>
            <ResultItem 
              label="Площадь стен" 
              value={`${formatNumber(results.wallsArea, 2)} м²`} 
              icon={<Square className="w-4 h-4"/>}
            />
            
            {results.ceilingArea && (
              <ResultItem 
                label="Площадь потолка" 
                value={`${formatNumber(results.ceilingArea, 2)} м²`} 
                icon={<Square className="w-4 h-4"/>}
              />
            )}
            
            <ResultItem 
              label="Площадь окон" 
              value={`${formatNumber(results.windowsArea, 2)} м²`} 
              icon={<Square className="w-4 h-4"/>}
            />
            
            <ResultItem 
              label="Площадь дверей" 
              value={`${formatNumber(results.doorsArea, 2)} м²`} 
              icon={<Square className="w-4 h-4"/>}
            />
          </>
        )}
        
        <ResultItem 
          label="Тип краски" 
          value={results.paintType} 
          color="green" 
          icon={<Droplets className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Качество краски" 
          value={results.paintQuality} 
          color="green" 
        />
        
        <ResultItem 
          label="Тип поверхности" 
          value={results.surfaceType} 
        />
        
        <ResultItem 
          label="Состояние поверхности" 
          value={results.surfaceCondition} 
        />
        
        <ResultItem 
          label="Количество слоев" 
          value={results.coatsNumber} 
          color="indigo" 
          icon={<Layers className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Расход краски на 1 м² (1 слой)" 
          value={`${formatNumber(1 / results.coveragePerLiter, 2)} л/м²`} 
          color="indigo" 
        />
        
        <ResultItem 
          label="Цена за литр" 
          value={`${formatNumber(results.pricePerLiter, 0)} ₽`} 
          color="orange" 
          icon={<CircleDollarSign className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Общая стоимость краски" 
          value={`${formatNumber(results.totalCost, 0)} ₽`} 
          color="orange" 
        />
        
        {(results.includeBaseboard || results.includeStucco) && (
          <ResultItem 
            label="Дополнительные элементы" 
            value={[
              results.includeBaseboard ? `Плинтус (${results.baseboardLength} м)` : null,
              results.includeStucco ? `Лепнина (${results.stuccoLength} м)` : null
            ].filter(Boolean).join(', ')} 
          />
        )}

        {/* Примечание о расчете */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Droplets className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">О расчете расхода краски</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Расчет является приблизительным и зависит от множества факторов: фактического состояния поверхности, техники нанесения, толщины слоя и других особенностей. Рекомендуется всегда приобретать краску с запасом 10-15%.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;